import { APPLICATION_PHASES, RecommendationSchema } from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  EmptyState,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@paguz/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  FilterXIcon,
  InboxIcon,
  UserCheckIcon,
} from 'lucide-react';
import { useState } from 'react';
import type { ApplicationsSearch } from '../../app/search.ts';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import type { AdminApplicationSummary } from '../../api/origination-schemas.ts';
import { Checkbox, Field, Select } from '../../components/form-controls.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { shortId } from '../../lib/format.ts';
import { APPLICATION_STATUS, PHASE_LABEL, RECOMMENDATION } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { slaFor, useConsoleNow, type SlaTone } from '../../lib/sla.ts';
import { ROUTE_LABEL } from './labels.ts';

const route = getRouteApi('/app/expedientes');

const SLA_DOT: Readonly<Record<SlaTone, string>> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-destructive',
};

/** Cola de expedientes por fase y SLA (`06-backoffice.md` §4.2). */
export function ApplicationsPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const query = useQuery({
    queryKey: queryKeys.applications(search),
    queryFn: () => api.listApplications(search),
    placeholderData: keepPreviousData,
  });
  const setFilters = (next: ApplicationsSearch) => {
    void navigate({ search: { ...next, cursor: undefined } });
  };
  const hasFilters = Object.values(search).some(Boolean);
  const waiting = query.data?.data.filter((a) => a.status === 'SUBMITTED').length ?? 0;

  return (
    <>
      <PageHeader
        title="Expedientes"
        description="Solicitudes de cupo preparadas por el Asesor IA. Por debajo del umbral de la política se autoaprueban; las demás esperan tu decisión."
        actions={
          waiting > 0 && (
            <Badge variant="highlight" className="text-sm">
              {waiting} por decidir
            </Badge>
          )
        }
      />
      <Filters search={search} onChange={setFilters} />
      <Panel className="mt-5" bodyClassName="p-0">
        {query.isPending ? (
          <div className="p-5">
            <LoadingRows />
          </div>
        ) : query.isError ? (
          <div className="p-5">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : query.data.data.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={<InboxIcon />}
              title="No hay expedientes con esos filtros"
              description="Cuando el Asesor IA envíe una solicitud a decisión aparecerá aquí."
              action={
                hasFilters && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFilters({});
                    }}
                  >
                    <FilterXIcon /> Limpiar filtros
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <QueueTable rows={query.data.data} dim={query.isPlaceholderData} />
        )}
      </Panel>
    </>
  );
}

function Filters({
  search,
  onChange,
}: {
  search: ApplicationsSearch;
  onChange: (next: ApplicationsSearch) => void;
}) {
  return (
    <Panel bodyClassName="flex flex-wrap items-end gap-4">
      <Field label="Fase" htmlFor="f-phase" className="w-52">
        <Select
          id="f-phase"
          value={search.phase ?? ''}
          onChange={(e) => {
            onChange({
              ...search,
              phase: (e.target.value || undefined) as ApplicationsSearch['phase'],
            });
          }}
        >
          <option value="">Todas</option>
          {APPLICATION_PHASES.map((p) => (
            <option key={p} value={p}>
              {PHASE_LABEL[p]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Recomendación del motor" htmlFor="f-rec" className="w-52">
        <Select
          id="f-rec"
          value={search.recommendation ?? ''}
          onChange={(e) => {
            onChange({
              ...search,
              recommendation: (e.target.value || undefined) as ApplicationsSearch['recommendation'],
            });
          }}
        >
          <option value="">Todas</option>
          {RecommendationSchema.options.map((r) => (
            <option key={r} value={r}>
              {RECOMMENDATION[r].label}
            </option>
          ))}
        </Select>
      </Field>
      <Checkbox
        label="Con alertas"
        checked={search.withAlerts === 'true'}
        onCheckedChange={(on) => {
          onChange({ ...search, withAlerts: on ? 'true' : undefined });
        }}
      />
      <Checkbox
        label="Asignados a mí"
        checked={search.assignedTo === 'me'}
        onCheckedChange={(on) => {
          onChange({ ...search, assignedTo: on ? 'me' : undefined });
        }}
      />
    </Panel>
  );
}

function QueueTable({ rows, dim }: { rows: readonly AdminApplicationSummary[]; dim: boolean }) {
  const now = useConsoleNow();
  const canOperate = useCan('applications:operate');
  const queryClient = useQueryClient();
  const [taking, setTaking] = useState<string | null>(null);
  const take = useMutation({
    mutationFn: (id: string) => api.assignApplication(id),
    onMutate: (id) => {
      setTaking(id);
    },
    onSuccess: (detail) => {
      queryClient.setQueryData(queryKeys.application(detail.application.id), detail);
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      toast.success('Expediente asignado a ti');
    },
    onError: (error) => toast.error(describeError(error)),
    onSettled: () => {
      setTaking(null);
    },
  });

  return (
    <Table className={cn(dim && 'opacity-60')}>
      <TableHeader>
        <TableRow>
          <TableHead>SLA</TableHead>
          <TableHead>Solicitante</TableHead>
          <TableHead>Fase</TableHead>
          <TableHead className="text-right">Solicitado</TableHead>
          <TableHead className="text-right">Cupo sugerido</TableHead>
          <TableHead>Motor</TableHead>
          <TableHead>Ruta</TableHead>
          <TableHead className="text-center">Alertas</TableHead>
          <TableHead>Analista</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const sla = slaFor(row.phaseEnteredAt, now);
          const open = row.status === 'SUBMITTED';
          return (
            <TableRow key={row.id} className={cn(!open && 'text-muted-foreground')}>
              <TableCell>
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  <span
                    aria-hidden
                    className={cn('size-2.5 rounded-full', open ? SLA_DOT[sla.tone] : 'bg-muted')}
                  />
                  <span className="sr-only">
                    {sla.tone === 'success'
                      ? 'En tiempo'
                      : sla.tone === 'warning'
                        ? 'Por vencer'
                        : 'Vencido'}
                  </span>
                  {sla.label}
                </span>
              </TableCell>
              <TableCell>
                <Link
                  to="/expedientes/$applicationId"
                  params={{ applicationId: row.id }}
                  className="font-semibold text-primary hover:underline"
                >
                  {row.applicant.fullName}
                </Link>
                <div className="text-xs text-muted-foreground">
                  {row.businessName ?? '—'} · {row.city ?? '—'} ·{' '}
                  <code className="font-mono" title={row.id}>
                    {shortId(row.id)}
                  </code>
                </div>
              </TableCell>
              <TableCell>
                <div className="font-medium text-foreground">{PHASE_LABEL[row.phase]}</div>
                <Badge variant={APPLICATION_STATUS[row.status].tone} className="mt-1">
                  {APPLICATION_STATUS[row.status].label}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {row.requestedAmount ? (
                  <MoneyText value={row.requestedAmount.amount} size="sm" />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell className="text-right">
                {row.suggestedLimit ? (
                  <MoneyText
                    value={row.suggestedLimit.amount}
                    size="sm"
                    className="font-semibold"
                  />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                {row.recommendation ? (
                  <Badge variant={RECOMMENDATION[row.recommendation].tone}>
                    {RECOMMENDATION[row.recommendation].label}
                  </Badge>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell className="text-sm">
                {row.policyRoute ? ROUTE_LABEL[row.policyRoute] : '—'}
              </TableCell>
              <TableCell className="text-center">
                {row.alerts > 0 ? (
                  <span className="inline-flex items-center gap-1 font-semibold text-warning-soft-foreground">
                    <AlertTriangleIcon className="size-4" /> {row.alerts}
                  </span>
                ) : (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {row.assignedTo?.name ??
                  (open && canOperate ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={taking === row.id}
                      onClick={() => {
                        take.mutate(row.id);
                      }}
                    >
                      <UserCheckIcon /> Tomar
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">Sin asignar</span>
                  ))}
              </TableCell>
              <TableCell>
                <Link
                  to="/expedientes/$applicationId"
                  params={{ applicationId: row.id }}
                  aria-label={`Abrir expediente de ${row.applicant.fullName}`}
                  className="text-muted-foreground hover:text-primary"
                >
                  <ChevronRightIcon className="size-5" />
                </Link>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
